import React, { useState } from 'react'
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react'
import {
  Bars3Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline'

import UserMenu from './UserMenu'
import Navbar from './Navbar'
import type { UserData } from '../../../services/User'

interface NavigationItem {
  name: string
  href: string
  icon?: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>
  children?: NavigationItem[]
  badge?: string
  status?: 'submitted' | 'pending'
}

interface UserNavigationItem {
  name: string
  href?: string
  onClick?: () => void
}

interface SidebarLayoutProps {
  LayoutChildren: React.ComponentType
  navigation: NavigationItem[]
  userNavigation: UserNavigationItem[]
  user: UserData
  currentProduct: string
  NotificationMenu?: React.ComponentType<{ dropdownPosition?: string }>
  pathname: string
  Link: React.ComponentType<{ to: string; className?: string; children?: React.ReactNode }>
  userNamePostfix?: string
}

const SidebarLayout = ({ LayoutChildren, navigation, userNavigation, user, currentProduct, NotificationMenu, pathname, Link, userNamePostfix }: SidebarLayoutProps) => {
  const [sidebarOpen, setSidebarOpen] = useState(false)

  return (
    <>
      <Transition show={sidebarOpen}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
          <TransitionChild
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/80" />
          </TransitionChild>

          <div className="fixed inset-0 flex">
            <TransitionChild
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <DialogPanel className="relative mr-16 flex w-full max-w-xs flex-1">
                <TransitionChild
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                    <button type="button" onClick={() => setSidebarOpen(false)} className="-m-2.5 p-2.5">
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon aria-hidden="true" className="size-6 text-white" />
                    </button>
                  </div>
                </TransitionChild>

                <Navbar
                  navigation={navigation}
                  userNavigation={userNavigation}
                  user={user}
                  currentProduct={currentProduct}
                  NotificationMenu={NotificationMenu}
                  pathname={pathname}
                  Link={Link}
                  userNamePostfix={userNamePostfix}
                />

              </DialogPanel>
            </TransitionChild>
          </div>
        </Dialog>
      </Transition>


      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col">
        <Navbar
          navigation={navigation}
          userNavigation={userNavigation}
          user={user}
          currentProduct={currentProduct}
          NotificationMenu={NotificationMenu}
          pathname={pathname}
          Link={Link}
        />
      </div>

      <div className="sticky top-0 z-40 flex w-full justify-between bg-gray-200 px-4 py-4 shadow-xs sm:px-6 lg:hidden">
        <button type="button" onClick={() => setSidebarOpen(true)} className="-m-2.5 p-2.5 text-slate-600 lg:hidden">
          <span className="sr-only">Open sidebar</span>
          <Bars3Icon aria-hidden="true" className="size-6" />
        </button>
        <div className='flex flex-row space-x-2'>
          {NotificationMenu && <NotificationMenu />}
          <UserMenu dropdownPosition='notBottomLeft' userNavigation={userNavigation} userNamePostfix={userNamePostfix} />
        </div>
      </div>

      <main className="py-10 lg:pl-72">
        <div className="px-4 sm:px-6 lg:px-8">
          <LayoutChildren />
        </div>
      </main>
    </>
  )
}

export default SidebarLayout
